import createClient, { type Middleware } from 'openapi-fetch';
import { t } from '../i18n';
import type { components, paths } from './schema';

/**
 * Typed REST client for `contracts/openapi/api.yaml` (types generated with `npm run api:gen`).
 * Every call goes to `/api/v1` with the session cookie; errors use the API error envelope
 * `{"error": {"code", "message", "fields?", "request_id?"}}` and are thrown as ApiError by `call()`.
 */
export type Schemas = components['schemas'];
export type ErrorCode = Schemas['ErrorCode'];

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: ErrorCode | 'network',
    message: string,
    public fields: Record<string, string> = {},
    public requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
  /** licence read-only (422 with fields.licence = read_only) or locked */
  get licence(): string | undefined {
    return this.fields.licence;
  }
}

type Listener = (e: ApiError) => void;
const listeners = new Set<Listener>();
/** Global API events: unauthenticated (401), licence locked, permission changes. */
export function onApiError(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

const PUBLIC = /^\/(auth\/|i18n\/|languages$|version$)/;

const errors: Middleware = {
  async onResponse({ request, response }) {
    if (response.ok) return undefined;
    const path = new URL(request.url).pathname.replace(/^\/api\/v1/, '');
    let body: any = null;
    try {
      body = await response.clone().json();
    } catch {
      /* not JSON */
    }
    const e = new ApiError(
      response.status,
      body?.error?.code || (response.status === 401 ? 'unauthenticated' : 'internal'),
      body?.error?.message || noAnswer(),
      body?.error?.fields || {},
      body?.error?.request_id,
    );
    if ((response.status === 401 && !PUBLIC.test(path)) || e.licence === 'locked') listeners.forEach((l) => l(e));
    return undefined;
  },
};

/** Text of a failure without the API's error envelope (a proxy, a route that does not exist): never raw English. */
const noAnswer = () => t('app.error.no-answer', 'The server did not answer this request. Try again in a moment.');

export const api = createClient<paths>({ baseUrl: '/api/v1', credentials: 'include' });
api.use(errors);

/**
 * Await an openapi-fetch call and return its data, or throw ApiError.
 *   const d = await call(api.GET('/dashboard', { params: { query: { period: 'today' } } }));
 */
export async function call<T>(p: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  let r: { data?: T; error?: unknown; response: Response };
  try {
    r = await p;
  } catch (err) {
    throw new ApiError(0, 'network', err instanceof Error ? err.message : String(err));
  }
  if (r.response.ok) return r.data as T;
  const b = r.error as any;
  throw new ApiError(
    r.response.status,
    b?.error?.code || 'internal',
    b?.error?.message || noAnswer(),
    b?.error?.fields || {},
    b?.error?.request_id,
  );
}

/**
 * A 404 without the API's error envelope: the route does not exist on this server. Unlike the API's own
 * `not_found`, it says nothing about the object asked for, so it must not read as "account not found".
 */
export const isMissingRoute = (e: ApiError | null | undefined) => e?.status === 404 && e.code !== 'not_found';

/** Idempotency key for state-changing requests that must not run twice (actions). */
export function idempotencyKey(): string {
  return (crypto as Crypto & { randomUUID?: () => string }).randomUUID?.() || Date.now().toString(36) + Math.random().toString(36).slice(2);
}
